import { useCallback, useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Search } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { JobCard } from "../components/jobs/JobCard";
import { FabricStock } from "../components/jobs/FabricStock";
import { JobSummary } from "../components/jobs/JobSummary";
import { ShirtStock } from "../components/jobs/ShirtStock";
import { PageHead } from "../components/FormBits";
import { DailySummary } from "../components/jobs/StockExtras";
import { useAuth } from "../context/AuthContext";
import { ExportButton } from "../components/ExportButton";

const VIEWS = [["jobs", "Job orders", "jobs_view"], ["stock", "Fabric stock", "jobs_view"], ["summary", "Given vs recd", "jobs_view"],
  ["shirts", "Stock", "stock_view"], ["daily", "Daily summary", "stock_view"]];

const JobList = ({ initialOpen }) => {
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("Open");
  const [list, setList] = useState(null);
  const [open, setOpen] = useState(initialOpen);
  const load = useCallback(() => {
    api.get("/jobs", { params: { q, status } }).then((r) => setList(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [q, status]);
  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);
  const { can } = useAuth();
  return (
    <div className="space-y-3">
      {can("jobs_write") && <Link to="/jobs/new" className="btn btn-primary w-full" data-testid="new-job-btn"><Plus size={18} /> Issue fabric to fabricator</Link>}
      <ExportButton name="jobs" label="Jobs Excel" className="w-full" />
      <div className="relative">
        <Search size={18} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <input className="field !pl-10" placeholder="Search job, fabricator, fabric, style" value={q} onChange={(e) => setQ(e.target.value)} data-testid="job-search-input" />
      </div>
      <div className="seg">
        {["Open", "Closed", "All"].map((s) => <button key={s} data-active={status === s} onClick={() => setStatus(s)} data-testid={`job-filter-${s.toLowerCase()}`}>{s}</button>)}
      </div>
      {!list ? <p className="text-muted-foreground">Loading…</p>
        : !list.length ? <p className="panel p-6 text-center text-muted-foreground" data-testid="job-empty">No job orders.</p>
        : list.map((j) => <JobCard key={j.id} job={j} open={open === j.id} onToggle={() => setOpen(open === j.id ? null : j.id)} onChanged={load} />)}
    </div>
  );
};

export default function Jobs() {
  const location = useLocation();
  const { can } = useAuth();
  const views = VIEWS.filter((v) => can(v[2]));
  const [picked, setView] = useState(location.state?.view);
  const view = views.some((v) => v[0] === picked) ? picked : views[0]?.[0];
  return (
    <div className="space-y-4" data-testid="jobs-page">
      <PageHead kicker={can("jobs_view") ? "Third-party fabricators" : "Godowns & showrooms"} title={can("jobs_view") ? "Job work" : "Stock"}
        sub={can("jobs_view") ? "Fabric given to fabricators and shirts received back." : "Stock, delivery challans and sales at every location."} />
      <div className="grid gap-1 rounded-xl border bg-card p-1" style={{ gridTemplateColumns: `repeat(${views.length}, minmax(0, 1fr))` }}>
        {views.map(([k, label]) => (
          <button key={k} onClick={() => setView(k)} data-testid={`jobs-view-${k}`}
            className={`min-h-[44px] rounded-lg px-1 text-xs font-semibold leading-tight transition-colors sm:text-sm ${view === k ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}>{label}</button>
        ))}
      </div>
      {view === "jobs" && <JobList initialOpen={location.state?.open || null} />}
      {view === "stock" && <FabricStock />}
      {view === "summary" && <JobSummary />}
      {view === "shirts" && <ShirtStock />}
      {view === "daily" && <DailySummary />}
    </div>
  );
}
